// Trade add-on page — homeowner-facing /trade-addendum route (Oct 29, 2026), modelled on ColorSelection (/colors).
//
// Greg, Oct 29, 2026: "if a trade is added after the homeowner has signed, the addendum is made. could we treat that
// similar to the color confirmation text a homeowner gets ... the text sends link for them to confirm and sign ...
// sign brings up addendum, work and price, homeowner signs and is sent a link to open and sign. once signed, the
// schedule button is available. if signed later, the sales person is texted with link to open and click schedule
// button."
//
// The link (build-calendar trade_addendum_send) is /trade-addendum?token=<the /colors token>&trade=<the job's id>.
// The page reads the deal's add-ons through ho_trade_addenda (the work's words and the price to the homeowner, never
// a sub figure), shows the one the link names (or the only one waiting), and on Sign: the signature image to
// deal-documents/signatures/, the one-page PDF (lib/tradeAddendumPDF) to deal-documents/trade-addendums/<deal id>/,
// then ho_record_trade_addendum, which refuses if the price or the words changed since the page opened, and then
// ho-portal-notify (trade_addendum_signed) texts whoever set the trade up to schedule it. Signed already: the
// confirmation and the PDF. Migration 20261029g.

import { useEffect, useMemo, useState } from "react";
import { useHomeownerAuth } from "./useHomeownerAuth";
import HomeownerGate from "./HomeownerGate";
import SignatureCanvas from "./SignatureCanvas";
import { supabaseAnon as supabase } from "../../lib/supabase";
import { authTriple, hoPortalNotify, hoRecordTradeAddendum, hoTradeAddenda, type HoTradeAddendum } from "../../lib/homeownerRpc";
import { TRADE_ADDENDUM_DUE, tradeAddendumTerms } from "../../lib/installAgreementTerms";
import { fmtAddendumAmount, tradeAddendumNumber } from "../../../supabase/functions/build-calendar/tradeText";

const CHANGED = "This add-on changed since you opened it. Please reload the page.";
const isOpen = (r: HoTradeAddendum) => r.status === "needs_signature" || r.status === "sent";
const amountOf = (r: HoTradeAddendum) => (r.amount === null || r.amount === undefined ? 0 : Number(r.amount));
const fmtDay = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "America/New_York" }) : "";

export default function TradeAddendum() {
  const auth = useHomeownerAuth();
  const { deal } = auth;
  const tradeParam = useMemo(() => new URLSearchParams(window.location.search).get("trade"), []);

  const [rows, setRows] = useState<HoTradeAddendum[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pick, setPick] = useState<string | null>(tradeParam);
  const [signedName, setSignedName] = useState("");
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  const [saved, setSaved] = useState<{ label: string; amount: number; url: string | null; signedAt: Date } | null>(null);

  useEffect(() => {
    if (!deal) return;
    let alive = true;
    hoTradeAddenda(authTriple(deal))
      .then((r) => { if (alive) setRows(r); })
      .catch(() => { if (alive) setLoadError("We couldn't load your add-on. Please open the link from your text again, or call (470) 823-8816."); });
    return () => { alive = false; };
  }, [deal]);

  const open = (rows ?? []).filter(isOpen);
  // The add-on the link names; else the only one waiting (the job's price can move to its other visit when one of
  // two visits is cancelled, which changes its id); else none, and the page lists what is waiting or signed.
  const selected = (rows ?? []).find((r) => r.trade_id === pick) ?? (open.length === 1 ? open[0] : null);

  const canSubmit = !!selected && isOpen(selected) && signedName.trim().length >= 2 && !!signatureData && !saving && !changed;

  const handleSubmit = async () => {
    if (!canSubmit || !deal || !selected) return;
    setSaving(true);
    setSaveError(null);
    try {
      const signedAt = new Date();
      const name = signedName.replace(/\s+/g, " ").trim();
      const amount = amountOf(selected);

      // 1. The signature image (deal-documents, as the color addendum does).
      let sigUrl: string | null = null;
      let sigPath: string | null = null;
      if (signatureData) {
        const sigBlob = await (await fetch(signatureData)).blob();
        sigPath = `signatures/${deal.id}/${Date.now()}-trade-addendum.png`;
        const { error: sigErr } = await supabase.storage.from("deal-documents").upload(sigPath, sigBlob, { contentType: "image/png", upsert: true });
        if (sigErr) throw new Error(`Signature upload failed: ${sigErr.message}`);
        const { data: signed } = await supabase.storage.from("deal-documents").createSignedUrl(sigPath, 31536000);
        sigUrl = signed?.signedUrl ?? null;
      }

      // 2. The one-page PDF: the work and its price as the page showed them.
      const addendumNumber = tradeAddendumNumber(deal.claim_number, deal.homeowner_name, selected.next_n);
      const { generateTradeAddendumPDF } = await import("../../lib/tradeAddendumPDF");
      const pdfBlob = await generateTradeAddendumPDF({
        deal, label: selected.label, amount, addendumNumber, signatureDataUrl: signatureData, signedName: name, signedAt,
      });
      const pdfPath = `trade-addendums/${deal.id}/${Date.now()}-signed.pdf`;
      // Upload only (an INSERT; Oct 30: anon has no read policy on trade-addendums/, so nobody can list the signed
      // add-ons with the public key).
      const { error: pdfErr } = await supabase.storage.from("deal-documents").upload(pdfPath, pdfBlob, { contentType: "application/pdf", upsert: false });
      if (pdfErr) throw pdfErr;
      // A signed URL (a year), made by ho-portal-notify after it checks the link: the bucket is private; it opens for
      // the homeowner and for staff.
      const urlRes = await hoPortalNotify(authTriple(deal), "trade_addendum_url", { pdf_path: pdfPath });
      const pdfUrl = urlRes.ok && typeof urlRes.data.url === "string" ? urlRes.data.url : null;
      if (!pdfUrl) throw new Error(`signed URL failed: ${urlRes.ok ? "no url" : urlRes.code}`);

      // 3. Record it: refused if the price or the words changed since the page opened.
      const { error: recErr } = await hoRecordTradeAddendum(authTriple(deal), {
        tradeId: selected.trade_id, amount, label: selected.label, signedName: name, pdfPath, url: pdfUrl,
        number: addendumNumber, signaturePath: sigPath, signatureUrl: sigUrl,
      });
      if (recErr) {
        if ((recErr.message ?? "").includes("changed since you opened it")) {
          setChanged(true);
          setSaveError(CHANGED);
          return;
        }
        if ((recErr.message ?? "").includes("can't be signed online")) {
          // The deal's commission is settled (Oct 30): the office takes it from here.
          setSaveError(recErr.message);
          return;
        }
        if ((recErr.message ?? "").includes("already signed")) {
          setRows((rs) => rs?.map((r) => (r.trade_id === selected.trade_id ? { ...r, status: "signed" } : r)) ?? rs);
          return;
        }
        throw recErr;
      }

      // 4. Tell whoever set the trade up, so they can schedule it. Best-effort: the add-on is already signed.
      const notify = await hoPortalNotify(authTriple(deal), "trade_addendum_signed", { trade_id: selected.trade_id });
      if (!notify.ok) console.warn("[trade-addendum] alert failed:", notify.code);

      setSaved({ label: selected.label, amount, url: pdfUrl, signedAt });
    } catch (err) {
      console.error("Trade add-on signing failed:", err);
      setSaveError("Something went wrong saving your signature. Please try again or call us at (470) 823-8816.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <HomeownerGate {...auth} title="Add-on to Your Roof Agreement">
      {deal && (
        <div className="space-y-4">
          {!rows && !loadError && <div className="h-40 rounded-xl bg-white/60 animate-pulse" />}
          {loadError && <div className="bg-white rounded-xl p-5 shadow-sm text-sm text-red-700">{loadError}</div>}

          {/* SIGNED JUST NOW */}
          {saved && (
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="text-4xl mb-3">✅</div>
              <h1 className="text-xl font-bold text-bardin-ink mb-2">Add-on Signed!</h1>
              <p className="text-gray-600 mb-1"><span className="font-semibold">{saved.label}</span></p>
              <p className="text-gray-600 mb-3">{fmtAddendumAmount(saved.amount)} · {TRADE_ADDENDUM_DUE}</p>
              <p className="text-sm text-gray-400 mb-4">
                Thank you. We'll schedule the work and let you know the day. A signed copy has been added to your project documents.
              </p>
              {saved.url && (
                <a href={saved.url} target="_blank" rel="noopener noreferrer"
                   className="inline-block bg-bardin-teal text-white text-sm font-medium rounded-lg px-4 py-2 hover:bg-bardin-teal/90 transition">
                  View signed add-on
                </a>
              )}
            </div>
          )}

          {/* ALREADY SIGNED */}
          {!saved && selected && selected.status === "signed" && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="text-3xl">✅</div>
                <h1 className="text-lg font-bold text-bardin-ink">Add-on Already Signed</h1>
              </div>
              <p className="text-sm text-gray-600"><span className="font-semibold">{selected.label}</span></p>
              <p className="text-sm text-gray-600 mb-3">
                {fmtAddendumAmount(amountOf(selected))}{selected.signed_at ? ` · signed ${fmtDay(selected.signed_at)}` : ""}{selected.signed_name ? ` by ${selected.signed_name}` : ""}
              </p>
              {selected.url && (
                <a href={selected.url} target="_blank" rel="noopener noreferrer"
                   className="inline-block bg-bardin-teal text-white text-sm font-medium rounded-lg px-4 py-2 hover:bg-bardin-teal/90 transition">
                  View signed add-on
                </a>
              )}
              <p className="mt-4 text-xs text-slate-500">We'll schedule the work and let you know the day. Questions? Call or text (470) 823-8816.</p>
            </div>
          )}

          {/* NOTHING PICKED: what is waiting, or that nothing is */}
          {!saved && rows && !selected && (
            <div className="bg-white rounded-xl p-5 shadow-sm">
              {open.length > 0 ? (
                <>
                  <h1 className="text-lg font-bold text-bardin-ink mb-2">Add-ons to your roof agreement</h1>
                  <p className="text-sm text-gray-500 mb-3">Pick one to review and sign.</p>
                  <div className="space-y-2">
                    {open.map((r) => (
                      <button key={r.trade_id} type="button" onClick={() => setPick(r.trade_id)}
                              className="w-full text-left rounded-lg border border-slate-200 px-3 py-2.5 hover:border-bardin-teal transition">
                        <span className="block text-sm font-semibold text-bardin-ink">{r.label}</span>
                        <span className="block text-xs text-slate-500">{fmtAddendumAmount(amountOf(r))}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <h1 className="text-lg font-bold text-bardin-ink mb-2">Nothing to sign right now</h1>
                  <p className="text-sm text-gray-600">
                    {(rows ?? []).some((r) => r.status === "signed")
                      ? "Your add-ons are signed. Thank you!"
                      : "There's no add-on waiting for your signature. If you were expecting one, call or text us at (470) 823-8816."}
                  </p>
                  {(rows ?? []).filter((r) => r.status === "signed" && r.url).map((r) => (
                    <a key={r.trade_id} href={r.url!} target="_blank" rel="noopener noreferrer" className="block mt-2 text-sm text-bardin-teal underline">
                      {r.label}: view the signed add-on
                    </a>
                  ))}
                </>
              )}
            </div>
          )}

          {/* THE ADD-ON TO SIGN */}
          {!saved && selected && isOpen(selected) && (
            <>
              <div className="bg-white rounded-xl p-5 shadow-sm">
                <h1 className="text-lg font-bold text-bardin-ink mb-1">Add-on to your roof agreement</h1>
                <p className="text-sm text-gray-500 mb-4">{deal.property_street}</p>
                <div className="rounded-lg bg-orange-50 border border-bardin-orange/40 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">The work</div>
                  <div className="text-base font-bold text-bardin-ink mt-0.5">{selected.label}</div>
                  <div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Price</div>
                  <div className="text-xl font-bold text-bardin-ink mt-0.5">{fmtAddendumAmount(amountOf(selected))}</div>
                  <div className="text-sm text-slate-600 mt-1">{TRADE_ADDENDUM_DUE}</div>
                </div>
                <h2 className="text-sm font-semibold text-bardin-ink mt-4 mb-1">Terms</h2>
                <p className="text-sm text-gray-600 leading-relaxed">{tradeAddendumTerms(!!deal.is_retail)}</p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm">
                <h2 className="text-lg font-bold text-bardin-ink mb-2">Sign the add-on</h2>
                <label className="block text-sm font-medium text-bardin-ink mb-1" htmlFor="trade-addendum-name">Your full name</label>
                <input id="trade-addendum-name" type="text" value={signedName} onChange={(e) => setSignedName(e.target.value)} maxLength={120}
                       autoComplete="name" placeholder="First and last name"
                       className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm mb-4 focus:ring-2 focus:ring-bardin-teal focus:border-transparent" />
                <label className="block text-sm font-medium text-bardin-ink mb-2">Your signature</label>
                <SignatureCanvas
                  onSign={(dataUrl) => setSignatureData(dataUrl)}
                  onClear={() => setSignatureData(null)}
                  defaultTypedName={signedName || deal.homeowner_name || ""}
                />

                {saveError && (
                  <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                    {saveError}
                    {changed && (
                      <button type="button" onClick={() => window.location.reload()} className="block mt-2 font-semibold underline">Reload the page</button>
                    )}
                  </div>
                )}

                <button type="button" onClick={handleSubmit} disabled={!canSubmit}
                        className="mt-4 w-full bg-bardin-orange text-white font-semibold rounded-lg py-3 hover:bg-bardin-orange/90 transition disabled:opacity-50 disabled:cursor-not-allowed">
                  {saving ? "Saving..." : signedName.trim().length < 2 ? "Type your full name to continue" : !signatureData ? "Sign above to continue" : "Sign the Add-on"}
                </button>
                <div className="mt-3 text-center text-xs text-slate-400">Questions? Reply to our text or call (470) 823-8816.</div>
              </div>
            </>
          )}
        </div>
      )}
    </HomeownerGate>
  );
}
